// Delete a parcel sent on memo by mistake (Memo Out › row ⋮ › Delete).
// Undoes everything sending it did, so no trace is left:
//   • the memo record (inventory/{item}/memos/{id}),
//   • the carats it held on the stone (memoCommittedCarat),
//   • its "Sent on Memo" line in the Code's history (Inventory Activity),
//   • its line in the consignment (customerMemos), whose ledger entry is
//     re-posted for what's left, or removed with the consignment when it
//     was the only line.
// Only for a memo still out: returned or sold memos are refused.

import {
  collection, doc, getDocs, query, runTransaction, serverTimestamp, where,
  type Firestore, type Timestamp,
} from 'firebase/firestore';
import { buildEntry, postEntry, entryId, linesMemoOut } from './ledger';
import type { CodeHistoryEntry, CustomerMemo, InventoryItem, InventoryMemo } from '../types';

const round2 = (n: number) => Math.round(n * 100) / 100;
// Same normalisation as the pages that write itemCodes/{code}/history.
const normalizeCode = (code: string) => code.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '_').slice(0, 400);

export async function deleteMemoRecord(db: Firestore, m: InventoryMemo, currency: string): Promise<void> {
  const itemRef = doc(db, 'inventory', m.inventoryItemId);
  const memoRef = doc(db, 'inventory', m.inventoryItemId, 'memos', m.id);
  // The matching history line (written with the memo, not linked by id):
  // same stone, client, date and carat; the one created closest in time.
  let histRef: ReturnType<typeof doc> | null = null;
  if (m.code) {
    const hs = await getDocs(query(collection(db, 'itemCodes', normalizeCode(m.code), 'history'), where('type', '==', 'memo-out')));
    const memoAt = (m as unknown as { createdAt?: Timestamp }).createdAt?.toMillis?.() ?? 0;
    const match = hs.docs
      .filter(d => {
        const h = d.data() as CodeHistoryEntry & { inventoryItemId?: string; entityId?: string };
        return h.inventoryItemId === m.inventoryItemId && h.entityId === m.entityId
          && h.date === m.date && Math.abs((h.carat ?? 0) - m.carat) < 1e-6;
      })
      .sort((a, b) => {
        const ta = (a.data().createdAt as Timestamp | undefined)?.toMillis?.() ?? 0;
        const tb = (b.data().createdAt as Timestamp | undefined)?.toMillis?.() ?? 0;
        return Math.abs(ta - memoAt) - Math.abs(tb - memoAt);
      })[0];
    if (match) histRef = match.ref;
  }
  const cmId = (m as InventoryMemo & { customerMemoId?: string }).customerMemoId;
  const cmRef = cmId ? doc(db, 'customerMemos', cmId) : null;

  await runTransaction(db, async tx => {
    const memoSnap = await tx.get(memoRef);
    const itemSnap = await tx.get(itemRef);
    const cmSnap   = cmRef ? await tx.get(cmRef) : null;
    if (!memoSnap.exists() || (memoSnap.data() as InventoryMemo).status !== 'active') {
      throw new Error('This memo has been returned or sold, so it can no longer be deleted.');
    }
    tx.delete(memoRef);
    if (itemSnap.exists()) {
      const cur = (itemSnap.data() as InventoryItem).memoCommittedCarat ?? 0;
      tx.update(itemRef, { memoCommittedCarat: Math.max(0, round2(cur - m.carat)), updatedAt: serverTimestamp() });
    }
    if (histRef) tx.delete(histRef);
    if (cmRef && cmSnap?.exists()) {
      const cm = cmSnap.data() as CustomerMemo;
      const lines = (cm.lines ?? []).filter(l => l.inventoryMemoId !== m.id);
      const jeRef = doc(db, 'journalEntries', entryId('memo_out', cmRef.id));
      if (lines.length === 0) {
        tx.delete(cmRef);
        tx.delete(jeRef);
      } else {
        const totalCarat     = round2(lines.reduce((t, l) => t + (l.carat ?? 0), 0));
        const totalAmountUSD = round2(lines.reduce((t, l) => t + (l.amountUSD ?? 0), 0));
        const totalCostUSD   = round2(lines.reduce((t, l) => t + (l.costUSD ?? 0), 0));
        tx.update(cmRef, { lines, totalCarat, totalAmountUSD, totalCostUSD, updatedAt: serverTimestamp() });
        if (totalCostUSD > 0.005) {
          postEntry(tx, db, buildEntry({
            businessId: cm.businessId, sourceType: 'memo_out', sourceId: cmRef.id,
            sourceCollection: 'customerMemos', date: cm.date, currency: currency, createdBy: cm.createdBy,
            note: `Memo out ${cm.memoNo}`,
            lines: linesMemoOut(totalCostUSD),
          }), entryId('memo_out', cmRef.id));
        } else {
          tx.delete(jeRef);
        }
      }
    }
  });
}
